import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { AnalyticsService } from './services/analytics.service';
import { AuthService } from './services/auth.service';
import { environment } from '../environments/environment';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule],
  template: `
    <router-outlet></router-outlet>

  `
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'openpyq';
  showDebugButton = false;
  private routerSubscription?: Subscription;
  private authSubscription?: Subscription;

  constructor(
    private analyticsService: AnalyticsService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Show debug button only on localhost
    this.showDebugButton = window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1';

    // Initialize Google Analytics 4
    this.analyticsService.initializeGA4(environment.ga4MeasurementId);

    // Wait a moment for GA4 to initialize, then track page view
    setTimeout(() => {
      console.log('✅ GA4 initialization timeout completed');

      // Track page view when app loads
      this.analyticsService.trackPageView('Home', '/');

      // Test anonymous user tracking immediately
      console.log('🧪 Testing anonymous user tracking...');
      this.analyticsService.trackAnonymousUserAction('app_loaded', {
        page: 'home',
        timestamp: new Date().toISOString()
      });
    }, 1000);

    // Add test methods accessible from browser console
    (window as any).testGA4 = () => {
      console.log('🧪 Manual GA4 Test Started');
      this.testGA4Events();
    };

    (window as any).testAnonymousFiltering = () => {
      console.log('🔍 Testing Anonymous User Filtering');
      this.testAnonymousFiltering();
    };

    // Track page views on route changes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Track page view
        const pageTitle = this.getPageTitle(event.urlAfterRedirects);
        this.analyticsService.trackPageView(pageTitle, event.urlAfterRedirects);

        // Track navigation
        const fromPage = this.getCurrentPageTitle();
        this.analyticsService.trackNavigation(fromPage, pageTitle);
      });

    // Track authentication state changes (if user logs in/out)
    // Note: This is a simple implementation - you may need to adjust based on your auth flow
    this.authSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const user = this.getUserInfo();
        if (user) {
          // User is logged in
          this.analyticsService.setUserProperties(user);
          this.analyticsService.trackUserLogin(user);
        } else {
          // Track anonymous user behavior
          this.analyticsService.trackAnonymousUserAction('browsing_while_logged_out');
        }
      });

    console.log('🚀 OpenPYQ Analytics initialized');
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  private getUserInfo(): any {
    const googleUser = localStorage.getItem('googleUser');
    if (googleUser) {
      try {
        return JSON.parse(googleUser);
      } catch {
        return null;
      }
    }
    return null;
  }

  private getCurrentPageTitle(): string {
    return document.title || 'OpenPYQ';
  }

  private getPageTitle(url: string): string {
    const titleMap: { [key: string]: string } = {
      '/': 'Home',
      '/questions': 'Questions',
      '/search': 'Search',
      '/practice': 'Practice',
      '/yearwise': 'Yearwise Questions',
      '/topics': 'Topics',
      '/subject': 'Subjects',
      '/about': 'About',
      '/contact': 'Contact'
    };

    // Handle dynamic routes
    if (url.includes('/questions/')) {
      return 'Question Details';
    }
    if (url.includes('/topics/')) {
      return 'Topic Questions';
    }
    if (url.includes('/yearwise/')) {
      return 'Yearwise Exam';
    }

    return titleMap[url] || document.title || 'OpenPYQ';
  }

  // Test GA4 events (accessible from browser console)
  testGA4Events(): void {
    console.log('🧪 Testing all analytics events...');

    // Test page view
    this.analyticsService.trackPageView('Test Page', '/test');

    // Test question view
    this.analyticsService.trackQuestionViewed({
      id: 'test_question_' + Date.now(),
      topicId: 'test_topic',
      questionNumber: 1
    });

    // Test topic selection
    this.analyticsService.trackTopicSelection({
      topic_id: 'test_topic_' + Date.now(),
      topic_name: 'Test Topic',
      item_level: 1,
      parent_topic_id: 'root'
    });

    // Test exam selection
    this.analyticsService.trackExamSelection({
      exam_name: 'Test Exam',
      paper_name: 'Test Paper',
      year: 2024
    });

    // Test search
    this.analyticsService.trackSearch('test search term', 5);

    // Test file download
    this.analyticsService.trackFileDownload({
      name: 'test_file.pdf',
      type: 'application/pdf',
      size: 1024000,
      operation: 'download',
      user_type: 'anonymous'
    });

    console.log('✅ All test events sent');
  }

  // Test anonymous user filtering
  testAnonymousFiltering(): void {
    console.log('🔍 Testing anonymous user filtering events...');

    this.analyticsService.trackAnonymousUserAction('manual_test', {
      test_type: 'anonymous_filtering',
      timestamp: new Date().toISOString(),
      page: window.location.pathname
    });

    this.analyticsService.trackAnonymousUserAction('filter_test', {
      filter_type: 'user_status_anonymous',
      expected_result: 'should_appear_in_anonymous_reports',
      test_id: 'anon_filter_' + Date.now()
    });

    console.log('✅ Anonymous filtering test events sent');
  }
}
